import type * as React from "react";
import { cn } from "@/lib/utils";
import { Label } from "@/components/ui/label";

interface FieldProps extends React.HTMLAttributes<HTMLDivElement> {
	children: React.ReactNode;
	orientation?: "vertical" | "horizontal";
}

function Field({ className, children, orientation = "vertical", ...props }: FieldProps) {
	return (
		<div
			className={cn(
				orientation === "horizontal" ? "flex items-center gap-2" : "space-y-2",
				className,
			)}
			{...props}
		>
			{children}
		</div>
	);
}

interface FieldGroupProps extends React.HTMLAttributes<HTMLDivElement> {
	children: React.ReactNode;
}

function FieldGroup({ className, children, ...props }: FieldGroupProps) {
	return (
		<div className={cn("space-y-4", className)} {...props}>
			{children}
		</div>
	);
}

interface FieldSetProps extends React.HTMLAttributes<HTMLFieldSetElement> {
	children: React.ReactNode;
}

function FieldSet({ className, children, ...props }: FieldSetProps) {
	return (
		<fieldset className={cn("space-y-4", className)} {...props}>
			{children}
		</fieldset>
	);
}

interface FieldLegendProps extends React.HTMLAttributes<HTMLLegendElement> {
	children: React.ReactNode;
}

function FieldLegend({ className, children, ...props }: FieldLegendProps) {
	return (
		<legend className={cn("text-lg font-medium", className)} {...props}>
			{children}
		</legend>
	);
}

function FieldSeparator({ className, ...props }: React.HTMLAttributes<HTMLHRElement>) {
	return <hr className={cn("border-border", className)} {...props} />;
}

interface FieldLabelProps extends React.ComponentProps<typeof Label> {
	children: React.ReactNode;
	required?: boolean;
}

function FieldLabel({ children, required, className, ...props }: FieldLabelProps) {
	return (
		<Label className={cn("text-sm font-medium", className)} {...props}>
			{children}
			{required && <span className="text-destructive ml-1">*</span>}
		</Label>
	);
}

interface FieldDescriptionProps extends React.HTMLAttributes<HTMLParagraphElement> {
	children?: React.ReactNode;
}

function FieldDescription({ children, className, ...props }: FieldDescriptionProps) {
	if (!children) return null;

	return (
		<p className={cn("text-sm text-muted-foreground", className)} {...props}>
			{children}
		</p>
	);
}

interface FieldErrorProps extends React.HTMLAttributes<HTMLParagraphElement> {
	children?: React.ReactNode;
}

function FieldError({ children, className, ...props }: FieldErrorProps) {
	if (!children) return null;

	return (
		<p className={cn("text-sm text-destructive", className)} {...props}>
			{children}
		</p>
	);
}

export {
	Field,
	FieldGroup,
	FieldSet,
	FieldLegend,
	FieldSeparator,
	FieldLabel,
	FieldDescription,
	FieldError,
};
